import { useState, type CSSProperties, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { Pagination } from '@/shared/ui/Pagination';
import { SortableTh } from '@/shared/ui/SortableTh';
import { fetchHistoryList } from './api';

interface HistoryRow extends Record<string, unknown> {
  id: number;
  operator_id?: number;
  operator_name?: string;
  user_id?: number;
  user_name?: string;
  create_time?: number;
  ip?: string;
}

type SortOrder = 'asc' | 'desc';

const SEARCH_KEY_OPTIONS = [
  { value: 'operator_id', label: 'Поиск по id оператора' },
  { value: 'user_id', label: 'Поиск по id пользователя' },
  { value: 'ip', label: 'Поиск по ip оператора' },
];

const emptyFilters = { searchKey: '', searchValue: '' };

const labelStyle: CSSProperties = { display: 'block', fontWeight: 600, marginBottom: 4 };

const dateFormat = new Intl.DateTimeFormat('ru', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  hour12: false,
  timeZone: 'Europe/Moscow',
});

export const HistoryPage = () => {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [draft, setDraft] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);

  const listParams = {
    page,
    sort_field: sortField,
    sort_order: sortOrder,
    operator_id: applied.searchKey === 'operator_id' ? applied.searchValue : '',
    user_id: applied.searchKey === 'user_id' ? applied.searchValue : '',
    ip: applied.searchKey === 'ip' ? applied.searchValue : '',
  };

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['history', listParams],
    queryFn: () => fetchHistoryList(listParams),
  });

  const rows = getListRows<HistoryRow>(data, ['items']);
  const totalPages = getTotalPages(data);

  const toggleSort = (field: string) => {
    setSortOrder(sortField === field && sortOrder === 'desc' ? 'asc' : 'desc');
    setSortField(field);
  };

  const sortTh = (field: string, label: string) => (
    <SortableTh active={sortField === field} onClick={() => toggleSort(field)}>
      {label}
    </SortableTh>
  );

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    setPage(1);
    setApplied({ ...draft });
  };

  const onReset = () => {
    setDraft(emptyFilters);
    setApplied(emptyFilters);
    setPage(1);
  };

  return (
    <>
      <section className="content-header">
        <h1>История</h1>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <h3 className="box-title">История посещений</h3>
            <form onSubmit={onSearch} style={{ marginTop: 12 }}>
              <div className="form-group">
                <span style={labelStyle}>Поиск</span>
                <FilterSelect
                  options={SEARCH_KEY_OPTIONS}
                  value={draft.searchKey}
                  onChange={(searchKey) => setDraft((p) => ({ ...p, searchKey }))}
                />
                <input
                  className="form-control input-sm"
                  style={{ marginTop: 6, width: 250 }}
                  placeholder="Поиск..."
                  value={draft.searchValue}
                  onChange={(e) => setDraft((p) => ({ ...p, searchValue: e.target.value }))}
                />
              </div>
              <button type="button" className="btn btn-warning" onClick={onReset} disabled={isFetching}>
                Очистить
              </button>{' '}
              <button type="submit" className="btn btn-primary" disabled={isFetching}>
                Найти
              </button>
            </form>
          </div>

          <div className="box-body">
            {isLoading && <p>Загрузка…</p>}
            {error && <div className="alert alert-danger">Ошибка загрузки истории</div>}
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-bordered table-hover">
                <thead>
                  <tr>
                    {sortTh('id', 'id')}
                    {sortTh('operator_id', 'id оператора')}
                    <th>Имя оператора</th>
                    {sortTh('user_id', 'id пользователя')}
                    <th>Имя пользователя</th>
                    {sortTh('create_time', 'Дата создания записи')}
                    {sortTh('ip', 'ip оператора')}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.id}</td>
                      <td>{row.operator_id}</td>
                      <td>{row.operator_name}</td>
                      <td>{row.user_id}</td>
                      <td>{row.user_name}</td>
                      <td>{row.create_time ? dateFormat.format(row.create_time * 1000) : ''}</td>
                      <td>{row.ip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        </div>
      </section>
    </>
  );
};
